if (!customElements.get('article-toc')) {
  const HEADINGS = 'h2, h3';
  const LINK_BASE =
    'sp-block sp-no-underline sp-text-sm sp-leading-snug sp-py-1.5 sp-pl-3 sp-border-l-2 sp-border-solid sp-transition-colors';
  const LINK_IDLE = 'sp-text-slate sp-border-transparent hover:sp-text-black';
  const LINK_ACTIVE = 'sp-text-jackOLantern sp-font-bold sp-border-jackOLantern hover:sp-text-jackOLantern ';

  const slugify = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'section';

  const headerOffset = () => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    const measured = parseInt(raw, 10) || 0;
    return Math.max(measured, window.innerWidth < 768 ? 64 : 72);
  };

  const SCROLL_GAP = 24;
  const READING_LINE = 0.25;

  customElements.define(
    'article-toc',
    class ArticleToc extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-toc-list]');
        this.toggle = this.querySelector('[data-toc-toggle]');
        this.body = document.querySelector('[data-article-body]');

        this.headings = this.body ? Array.from(this.body.querySelectorAll(HEADINGS)) : [];

        if (!this.list || this.headings.length === 0) {
          (this.closest('[data-toc-wrap]') || this).remove();
          return;
        }

        this.buildList();
        this.trackActive();

        if (this.toggle) {
          this.toggle.addEventListener('click', () => this.toggleList());
        }

        this.list.addEventListener('click', (e) => this.onLinkClick(e));
      }

      disconnectedCallback() {
        if (!this.onScroll) return;
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      buildList() {
        const used = new Set();

        this.links = this.headings.map((heading) => {
          if (!heading.id) {
            let id = slugify(heading.textContent);
            let suffix = 2;
            while (used.has(id) || document.getElementById(id)) {
              id = `${slugify(heading.textContent)}-${suffix}`;
              suffix += 1;
            }
            heading.id = id;
          }
          used.add(heading.id);

          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${heading.id}`;
          link.textContent = heading.textContent.trim();
          link.className = `${LINK_BASE} ${LINK_IDLE}`;
          item.appendChild(link);
          this.list.appendChild(item);

          return link;
        });
      }

      setActive(id) {
        this.links.forEach((link) => {
          const isActive = link.getAttribute('href') === `#${id}`;
          link.className = `${LINK_BASE} ${isActive ? LINK_ACTIVE : LINK_IDLE}`;
        });
      }

      trackActive() {
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            this.updateActive();
          });
        };

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.updateActive();
      }

      updateActive() {
        const line = window.innerHeight * READING_LINE;

        let active = this.headings[0];
        for (const heading of this.headings) {
          if (heading.getBoundingClientRect().top > line) break;
          active = heading;
        }

        if (active.id === this.activeId) return;
        this.activeId = active.id;
        this.setActive(active.id);
      }

      toggleList() {
        const isOpen = this.toggle.getAttribute('aria-expanded') === 'true';
        this.toggle.setAttribute('aria-expanded', String(!isOpen));
        this.list.hidden = isOpen;
      }

      onLinkClick(e) {
        const link = e.target.closest('a[href^="#"]');
        if (!link) return;

        const target = document.getElementById(link.getAttribute('href').slice(1));
        if (!target) return;

        e.preventDefault();

        if (this.toggle && this.toggle.getAttribute('aria-expanded') === 'true') {
          this.toggleList();
        }

        requestAnimationFrame(() => {
          const top =
            target.getBoundingClientRect().top + window.scrollY - headerOffset() - SCROLL_GAP;
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
        });
      }
    }
  );
}
